import { PremiumTierPreview } from '@src/pages/PremiumPage/PremiumTierPreview';
import { isTieredVariant } from '@lub0v/santa-utils';
import { useLocation, useNavigate } from 'react-router-dom';
import { PremiumOfferView } from '@src/components/PremiumUpsell/PremiumOfferView';
import { usePremiumInfoUrl } from '@src/utils/premiumReturn';
import { useStartPremiumPurchase } from './premiumInterest';
import { trackPremium } from '@src/utils/premiumAnalytics';
import { usePremiumOffer } from '@src/pages/PremiumPage/PremiumOfferProvider';
import { useCallback, useState, type ReactNode } from 'react';
import { Link } from '@src/components/Link/Link';
import { createPortal } from 'react-dom';
import { HeartIcon } from '@src/components/icons/HeartIcon';
import { useTranslation } from 'react-i18next';
import { Button } from '@src/components/Button/Button';
import { closeDialog, pushDialog } from '@src/components/Dialog/Dialog';
import { CrownIcon } from '@src/components/icons/CrownIcon';
import { UserExcludeIcon } from '@src/components/icons/UserExcludeIcon';
import { QuestionIcon } from '@src/components/icons/QuestionIcon';
import { WrenchIcon } from '@src/components/icons/WrenchIcon';
import { RefreshIcon } from '@src/components/icons/RefreshIcon';
import { EyeIcon } from '@src/components/icons/EyeIcon';
import { MessageIcon } from '@src/components/icons/MessageIcon';
import { StarIcon } from '@src/components/icons/StarIcon';
import { LinkIcon } from '@src/components/icons/LinkIcon';
import { ClockIcon } from '@src/components/icons/ClockIcon';
import { UsersIcon } from '@src/components/icons/UsersIcon';
import { HeadphonesIcon } from '@src/components/icons/HeadphonesIcon';
import { EyeOffIcon } from '@src/components/icons/EyeOffIcon';
import { GiftIcon } from '@src/components/icons/GiftIcon';
import { useMatchesMedia } from '@src/components/layout/useMobileHeaderRecede';
import { isDotCom } from '@src/site';
import './PremiumModal.css';

export type PremiumFeature = 'account' | 'address' | 'questions' | 'history' | 'themes' | 'organizers' | 'draw';
const features = [
  { id: 'noAds', icon: <EyeOffIcon /> },
  { id: 'address', icon: <LinkIcon /> },
  { id: 'themes', icon: <StarIcon /> },
  { id: 'drawControl', icon: <WrenchIcon /> },
  { id: 'questions', icon: <QuestionIcon /> },
  { id: 'organizers', icon: <UsersIcon /> },
  { id: 'history', icon: <ClockIcon /> },
  { id: 'support', icon: <HeadphonesIcon /> },
  { id: 'projectSupport', icon: <HeartIcon className="premium-modal__support-heart" /> },
] as const;

const freeFeatures = [
  { id: 'participants', icon: <UsersIcon /> },
  { id: 'draw', icon: <UserExcludeIcon /> },
  { id: 'questions', icon: <QuestionIcon /> },
  { id: 'drawControl', icon: <WrenchIcon /> },
  { id: 'themes', icon: <StarIcon /> },
  { id: 'organizers', icon: <WrenchIcon /> },
  { id: 'cards', icon: <EyeIcon /> },
  { id: 'guest', icon: <LinkIcon /> },
  { id: 'reset', icon: <RefreshIcon /> },
  { id: 'messages', icon: <MessageIcon /> },
] as const;

export function PremiumPlanTabs({ value, onChange, id = 'premium-tab', panelId = 'premium-mobile-comparison' }: { value: 'free' | 'premium'; onChange: (value: 'free' | 'premium') => void; id?: string; panelId?: string }) {
  const { t } = useTranslation();
  return (<div className="premium-modal__tabs" role="tablist" aria-label={t('premiumModal.comparison')}>
      {(['free', 'premium'] as const).map((plan) => <button key={plan} type="button" role="tab"
        id={`${id}-${plan}`} aria-selected={value === plan}
        aria-controls={panelId} onClick={() => onChange(plan)}
        onKeyDown={(event) => {
          if (['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) {
            event.preventDefault();
            const next = event.key === 'Home' ? 'free' : event.key === 'End' ? 'premium' : plan === 'free' ? 'premium' : 'free';
            onChange(next);
            document.getElementById(`${id}-${next}`)?.focus();
          }
        }} tabIndex={value === plan ? 0 : -1}>
        {plan === 'premium' && <CrownIcon />}{t(`premiumModal.${plan}`)}
      </button>)}
    </div>);
}

/** Same plan descriptions, icons and surfaces on the landing page and in the modal. */
export function PremiumPlanPanels({ mobile = false, mobilePlan = 'premium', premiumAction, freeAction, panelId, priceLabel, source = 'all', context, tabId = 'premium-tab' }: {
  source?: PremiumFeature | 'all'; context?: string; mobile?: boolean; mobilePlan?: 'free' | 'premium'; premiumAction: ReactNode; freeAction?: ReactNode; panelId?: string; priceLabel?: string; tabId?: string;
}) {
  const { t } = useTranslation();
  const offer = usePremiumOffer();
  const dotCom = isDotCom();
  const premiumFeatures = features.filter(({ id }) => id !== 'drawControl' && (id !== 'noAds' || !dotCom));
  premiumFeatures.splice(3, 0, features.find(({ id }) => id === 'drawControl')!);
  return (
    <div className="premium-modal__plan-panels" id={panelId} role={mobile ? 'tabpanel' : undefined} aria-labelledby={mobile ? `${tabId}-${mobilePlan}` : undefined}>
      {(['free', 'premium'] as const).filter((plan) => !mobile || plan === mobilePlan).map((plan) => <section key={plan} className={`premium-modal__plan premium-modal__plan--${plan}`} aria-label={t(`premiumModal.${plan}`)}>
        <header className="premium-modal__plan-heading">
          <div className="premium-modal__plan-title">
            <h3>{plan === 'premium' && <CrownIcon />}{t(plan === 'premium' ? 'premiumModal.premiumFullAccess' : 'premiumModal.free')}</h3>
          </div>
          <strong className="premium-modal__price">{plan === 'free' ? dotCom ? t('premiumModal.freePrice') : '0 ₽' : <PremiumOfferView placement="comparison" feature={source} context={context}>{priceLabel ?? offer.formattedPrice}</PremiumOfferView>}</strong>
          <span className={`premium-modal__timing-badge premium-modal__timing-badge--${plan}`}>
            {plan === 'free' && <span aria-hidden="true">♡</span>}
            {t(plan === 'free' ? 'premiumModal.freeForever' : 'premiumModal.season')}{plan === 'premium' && <sup>*</sup>}
          </span>
        {plan === 'premium' && <p className="premium-modal__includes-free">{t('premiumModal.includesFree')}</p>}
        </header>
        <ul className="premium-modal__feature-list">
          {(plan === 'free' ? freeFeatures : premiumFeatures).map(({ id, icon }) => <li key={id} data-testid={`premium-modal-${plan === 'free' ? 'free-' : ''}feature-${id}`}>
            <span className="premium-modal__list-icon" aria-hidden="true">{icon}</span>
            <div><strong>{plan === 'free' ? t(`premiumModal.freeFeatures.${id}.title`) : ['questions', 'themes', 'organizers'].includes(id) ? t(`premiumModal.mobile.premium.${id}`) : t(`premiumModal.rows.${id}.title`)}</strong>
              <small>{plan === 'free' ? t(`premiumModal.freeFeatures.${id}.description`) : id === 'themes' ? t('premiumModal.futureThemes') : t(`premiumModal.rows.${id}.description`)}</small>
              {plan === 'premium' && id === 'address' && <small className="premium-modal__address-example">https://company.santa-secret.{dotCom ? 'com' : 'ru'}</small>}
            </div>
          </li>)}
        </ul>
        {!mobile && <div className="premium-modal__plan-action">{plan === 'premium' ? premiumAction : freeAction ?? <Button fullWidth disabled>{t('premiumModal.current')}</Button>}</div>}
      </section>)}
    </div>
  );
}

export function PremiumComparison({ source, context }: { source: PremiumFeature; context?: string }) {
  const startPremiumPurchase = useStartPremiumPurchase();
  const premiumInfoHref = usePremiumInfoUrl();
  const creation = useLocation().pathname.replace(/\/$/, '') === '/box/new';
  const { t } = useTranslation();
  const offer = usePremiumOffer();
  const mobile = useMatchesMedia('(max-width: 48rem)');
  const [mobilePlan, setMobilePlan] = useState<'free' | 'premium'>('premium');
  const [footerHost, setFooterHost] = useState<HTMLElement | null>(null);
  const attachComparison = useCallback((node: HTMLDivElement | null) => {
    if (node) setFooterHost(node.closest<HTMLElement>('.dialog__card'));
  }, []);
  if (isTieredVariant(offer.variant)) return <div className="premium-comparison premium-comparison--tiered" data-source={source} onClick={event => { if ((event.target as Element).closest('a')) closeDialog(); }}><PremiumTierPreview embedded /></div>;
  const purchase = (mobile = false, bottom = false) => <Button
    className={`premium-modal__purchase${mobile ? ' premium-modal__purchase--top' : ''}`}
    fullWidth icon={<CrownIcon />} onClick={() => startPremiumPurchase(bottom ? 'comparison_bottom' : mobile ? 'comparison_top' : 'comparison', source, context)}
    testId={bottom ? 'premium-modal-purchase-bottom' : mobile ? 'premium-modal-purchase-top' : 'premium-modal-purchase'}>
    <PremiumOfferView placement={bottom ? 'comparison_bottom' : mobile ? 'comparison_top' : 'comparison'} feature={source} context={context}>{t('premiumModal.subscribePriced', { price: offer.symbolPrice })}</PremiumOfferView>
  </Button>;
  return <div ref={attachComparison} className="premium-comparison" data-source={source} data-mobile-plan={mobilePlan}>
    <div className="premium-modal__intro">
      <p className="premium-modal__intro-full">{t('premiumModal.intro')}</p><p className="premium-modal__intro-short">{t('premiumModal.introShort')}</p>
      <div className="premium-modal__art" aria-hidden="true">
        <span>{t('premiumModal.artNote')}</span>
        <svg className="premium-modal__art-arrow" viewBox="0 0 90 100" fill="none" aria-hidden="true">
          <path d="M67 6C31-1 9 17 14 38c4 18 25 11 18-1-8-13-23 7-13 24 7 12 23 21 47 27m-8-10 10 11-15 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        <div className="premium-modal__gift-cluster">
          <GiftIcon className="premium-modal__gift premium-modal__gift--large" />
          <GiftIcon className="premium-modal__gift premium-modal__gift--small" />
          <GiftIcon className="premium-modal__gift premium-modal__gift--tiny" />
        </div>
      </div>
    </div>
    <PremiumPlanTabs value={mobilePlan} onChange={setMobilePlan} />
    <PremiumPlanPanels source={source} context={context} mobile={mobile} mobilePlan={mobilePlan} premiumAction={purchase()} panelId="premium-mobile-comparison" />
    <footer className="premium-modal__footer">
      <div className="premium-modal__season-note" hidden={mobile && mobilePlan === 'free'}><span aria-hidden="true"><GiftIcon /></span><p><strong>{t('premiumModal.once')}</strong><small>* {t('premiumModal.access')}</small></p></div>
      <Link href={premiumInfoHref} target={creation ? '_blank' : undefined} premiumContext={{ placement: 'comparison_learn_more', feature: source, context, action: 'learn_more' }} onClick={closeDialog}>
        {t('premiumModal.learnEvenMore')}
      </Link>
    </footer>
    {mobile && footerHost && createPortal(<div className="premium-modal__fixed-action">
      {mobilePlan === 'premium' ? purchase(false, true) : <Button fullWidth disabled>{t('premiumModal.current')}</Button>}
    </div>, footerHost)}
  </div>;
}

export function usePremiumModal() {
  const navigate = useNavigate();
  const startPremiumPurchase = useStartPremiumPurchase();
  const offer = usePremiumOffer();
  const { t } = useTranslation();
  return (source: PremiumFeature, context: string | undefined, placement: string, intent: 'learn_more' | 'feature', options?: { comparison?: boolean }) => {
    trackPremium(intent === 'learn_more' ? 'premium_learn_more_click_v2' : 'premium_feature_gate_clicked_v2', { placement, feature: source, context });
    if (isTieredVariant(offer.variant) && !options?.comparison) {
      if (intent === 'feature') { startPremiumPurchase(placement, source, context); return; }
      navigate('/premium');
      return;
    }
    return pushDialog({
    title: t('premiumModal.title'),
    children: <PremiumComparison source={source} context={context} />,
    closeLabel: t('newBox.appearance.close'),
    testId: 'premium-modal',
    mobileFullScreen: true,
    });
  };
}
